import React, { useState } from 'react';
import {
  Paper, Typography, Box, Avatar, Button, Divider, Chip, IconButton, Tooltip,
} from '@mui/material';
import {
  Person as PersonIcon, Add as AddIcon, Close as CloseIcon,
  SupervisorAccount as ManagerIcon, Badge as AssociateIcon, AccountBalance as BankerIcon,
} from '@mui/icons-material';
import { ROLE_COLORS } from '../../config/constants';
import usePermissions from '../../hooks/usePermissions';
import { removeManager, removeAssociate, removeBanker } from '../../services/assignmentService';
import AssignManagerDialog from './AssignManagerDialog';
import AssignAssociateDialog from './AssignAssociateDialog';
import AssignBankerDialog from './AssignBankerDialog';

const roleIcons = {
  manager: <ManagerIcon sx={{ fontSize: 18 }} />,
  associate: <AssociateIcon sx={{ fontSize: 18 }} />,
  banker: <BankerIcon sx={{ fontSize: 18 }} />,
};

const AssignmentSection = ({ label, people, roleKey, color, canChange, onAdd, onRemove, studentId }) => {
  const [removing, setRemoving] = useState(null);

  const handleRemove = async (userId) => {
    setRemoving(userId);
    try {
      await onRemove(studentId, userId);
    } catch (err) {
      console.error('Remove failed:', err);
    } finally {
      setRemoving(null);
    }
  };

  return (
    <Box sx={{ py: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={{ bgcolor: color || 'grey.400', width: 32, height: 32 }}>
            {roleIcons[roleKey] || <PersonIcon sx={{ fontSize: 18 }} />}
          </Avatar>
          <Typography variant="body2" fontWeight={600} color="text.secondary">
            {label} ({people.length})
          </Typography>
        </Box>
        {canChange && (
          <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={onAdd} sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
            Add
          </Button>
        )}
      </Box>
      {people.length > 0 ? (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, ml: 5.5 }}>
          {people.map((p) => (
            <Chip
              key={p._id}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <span>{p.name}</span>
                  {p.bankName && <Typography variant="caption" color="text.secondary">({p.bankName})</Typography>}
                </Box>
              }
              size="small"
              sx={{ fontWeight: 600, fontSize: '0.8rem', bgcolor: `${color}15`, borderColor: color, color: color }}
              variant="outlined"
              onDelete={canChange ? () => handleRemove(p._id) : undefined}
              deleteIcon={
                removing === p._id
                  ? <Box sx={{ width: 18, height: 18, border: '2px solid', borderColor: color, borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite', '@keyframes spin': { '100%': { transform: 'rotate(360deg)' } } }} />
                  : <CloseIcon sx={{ fontSize: 16 }} />
              }
            />
          ))}
        </Box>
      ) : (
        <Box sx={{ ml: 5.5 }}>
          <Chip label="Not Assigned" size="small" variant="outlined" color="default" />
        </Box>
      )}
    </Box>
  );
};

const AssignmentPanel = ({ student, onRefresh }) => {
  const [managerDialogOpen, setManagerDialogOpen] = useState(false);
  const [associateDialogOpen, setAssociateDialogOpen] = useState(false);
  const [bankerDialogOpen, setBankerDialogOpen] = useState(false);
  const { canAssignManager, canAssignAssociate, canAssignBanker } = usePermissions();

  const handleAssigned = () => {
    setManagerDialogOpen(false);
    setAssociateDialogOpen(false);
    setBankerDialogOpen(false);
    if (onRefresh) onRefresh();
  };

  const handleRemoveAndRefresh = (removeFn) => async (studentId, userId) => {
    await removeFn(studentId, userId);
    if (onRefresh) onRefresh();
  };

  // Build arrays from new fields, falling back to single field for backward compat
  const managers = student?.managerIds?.length > 0
    ? student.managerIds
    : student?.managerId ? [student.managerId] : [];

  const associates = student?.associateIds?.length > 0
    ? student.associateIds
    : student?.associateId ? [student.associateId] : [];

  const bankers = student?.bankerIds?.length > 0
    ? student.bankerIds
    : student?.bankerId ? [student.bankerId] : [];

  return (
    <>
      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Assignments
        </Typography>
        <Divider sx={{ mb: 1 }} />

        <AssignmentSection
          label="Managers"
          people={managers}
          roleKey="manager"
          color={ROLE_COLORS.manager}
          canChange={canAssignManager}
          onAdd={() => setManagerDialogOpen(true)}
          onRemove={handleRemoveAndRefresh(removeManager)}
          studentId={student?._id}
        />

        <Divider />

        <AssignmentSection
          label="Associates"
          people={associates}
          roleKey="associate"
          color={ROLE_COLORS.associate}
          canChange={canAssignAssociate}
          onAdd={() => setAssociateDialogOpen(true)}
          onRemove={handleRemoveAndRefresh(removeAssociate)}
          studentId={student?._id}
        />

        <Divider />

        <AssignmentSection
          label="Bankers"
          people={bankers}
          roleKey="banker"
          color={ROLE_COLORS.banker}
          canChange={canAssignBanker}
          onAdd={() => setBankerDialogOpen(true)}
          onRemove={handleRemoveAndRefresh(removeBanker)}
          studentId={student?._id}
        />
      </Paper>

      <AssignManagerDialog open={managerDialogOpen} onClose={() => setManagerDialogOpen(false)} studentId={student?._id} onAssigned={handleAssigned} />
      <AssignAssociateDialog open={associateDialogOpen} onClose={() => setAssociateDialogOpen(false)} studentId={student?._id} onAssigned={handleAssigned} />
      <AssignBankerDialog open={bankerDialogOpen} onClose={() => setBankerDialogOpen(false)} studentId={student?._id} onAssigned={handleAssigned} />
    </>
  );
};

export default AssignmentPanel;
